import React, { CSSProperties, useEffect, useRef, useState } from 'react'
import { GoogleMap, useLoadScript } from '@react-google-maps/api'
import { isEqual, debounce } from 'lodash'
import { getBounds, getDistance } from 'geolib'
import MapMarker from './MapMarker'
import getConstants from '../../constants'
import ErrorBoundary from './ErrorBoundary'
import { Button } from 'shared-global-ec-uikit/components/dist/react/esm'

const containerStyle: CSSProperties = {
  border: 'none',
  position: 'relative',
  width: '100%',
  paddingBottom: '75%',
}

const { COUNTRY } = getConstants()

type OnChangeProps = {
  center: GeoCoordinateType
  zoom: number
  radius: number
}

export type OnChangeType = (props: OnChangeProps) => void
export type OnSearchThisAreaType = OnChangeType
export type Props = {
  className?: string
  style?: CSSProperties

  apiKey: string

  dimensions?: {
    width: string | number
    height: string | number
  }

  initialCenter: GeoCoordinateType
  initialZoom?: number
  singleMarkerZoom?: number

  storeList: Array<Components.Schemas.StoreList>
  selectedMarker?: number | null

  farFromCenterThreshold?: number
  isSearchThisAreaEnabled?: boolean
  isSearchingByMapArea?: boolean

  intlMessages: {
    searchThisArea: string
    getDirections: string
    distanceAway: string
  }

  onSearchThisArea?: OnSearchThisAreaType
  onStoreSelect?: (store: Components.Schemas.StoreDetail) => void
  onMarkerSelect?: (store: number) => void
  onClearMarker?: () => void
}

const StoresMap = ({
  className,
  style,
  apiKey,
  dimensions,
  initialZoom,
  initialCenter,
  singleMarkerZoom = 11,
  isSearchingByMapArea,
  isSearchThisAreaEnabled,
  storeList,
  intlMessages: { searchThisArea, getDirections, distanceAway },
  selectedMarker,
  onStoreSelect,
  onMarkerSelect,
  onClearMarker,
  onSearchThisArea,
}: Props) => {
  const { isLoaded } = useLoadScript({
    region: COUNTRY,
    googleMapsApiKey: apiKey,
  })
  const [googleMap, setGoogleMap] = useState<google.maps.Map>()
  const [center, setCenter] = useState(initialCenter)
  const [zoom, setZoom] = useState(initialZoom || 11)
  const [isDragging, setIsDragging] = useState(true)
  const [isSearchThisAreaAvailable, setIsSearchThisAreaAvailable] = useState(
    false,
  )
  const [radius, setRadius] = useState(0)
  const prevStoreList = useRef(storeList)

  useEffect(() => {
    const isStoreListDifferent = !isEqual(
      storeList.map(store => store.id),
      prevStoreList.current.map(store => store.id),
    )

    if (isStoreListDifferent) {
      const {
        center: centerValue,
        zoom: zoomValue,
      } = getCenterAndZoomFromStoreList()

      setCenter({
        latitude: centerValue.latitude,
        longitude: centerValue.longitude,
      })
      setZoom(zoomValue!)
      setIsSearchThisAreaAvailable(false)
      setRadius(0)
    }

    prevStoreList.current = storeList
  }, [storeList])

  const getCenterAndZoomFromStoreList = () => {
    // If the user is searching by map area ...
    if (isSearchingByMapArea) {
      // ... do not reposition the map based on store list.
      return { center, zoom }
    }

    // ... otherwise return initial center and zoom.
    if (!storeList.length) {
      return {
        center: {
          latitude: initialCenter.latitude,
          longitude: initialCenter.longitude,
        },
        zoom: initialZoom,
      }
    }

    // If there is only one store ...
    if (storeList.length === 1) {
      return {
        // ... use that store as center.
        center: {
          latitude: Number(storeList[0].latitude),
          longitude: Number(storeList[0].longitude),
        },
        zoom: singleMarkerZoom,
      }
    }

    // Get the bounds from the list of stores.
    const bounds = getBounds(
      storeList.map(store => ({
        latitude: Number(store.latitude),
        longitude: Number(store.longitude),
      })),
    )

    // determine the center and zoom that will allow all stores to be visible on the map.
    const centerLat = (bounds.minLat + bounds.maxLat) / 2
    const centerLng = (bounds.minLng + bounds.maxLng) / 2

    const latDiff = bounds.maxLat - bounds.minLat
    const lngDiff = bounds.maxLng - bounds.minLng

    const zoomValue = Math.min(
      Math.floor(Math.log2(360 / lngDiff)),
      Math.floor(Math.log2(180 / latDiff)),
    )
    return {
      center: { latitude: centerLat, longitude: centerLng },
      zoom: zoomValue,
    }
  }

  const handleCenterChanged = debounce(() => {
    if (googleMap?.getCenter() !== undefined) {
      const latLng = googleMap.getCenter()?.toJSON()

      if (latLng !== undefined)
        if (latLng.lat !== center.latitude || latLng.lng !== center.longitude)
          setCenter({ latitude: latLng.lat, longitude: latLng.lng })
    }
  }, 300)

  const handleBoundsChanged = debounce(() => {
    if (!googleMap) return

    const sw = googleMap
      .getBounds()
      ?.getSouthWest()
      .toJSON()
    const ne = googleMap
      .getBounds()
      ?.getNorthEast()
      .toJSON()
    if (ne && sw) {
      const geoLocationPresent = ne.lat && ne.lng && sw.lat && sw.lng
      // Get radius rounded to the nearest 25 km.
      const newRadius = geoLocationPresent
        ? Math.round(
            getDistance(
              { latitude: ne.lat, longitude: ne.lng },
              { latitude: sw.lat, longitude: sw.lng },
            ) /
              2 /
              1000 /
              25,
          ) * 25
        : 0
      setRadius(newRadius)
    }
  }, 300)

  const handleZoomChange = debounce(() => {
    if (!googleMap) return

    const newZoom = googleMap.getZoom()

    // Make Search This Area available ...
    const isSearchThisAreaAvailableValue =
      isSearchThisAreaAvailable ||
      // ... if the user zoomed out.
      zoom > newZoom!

    setZoom(newZoom!)
    setIsSearchThisAreaAvailable(isSearchThisAreaAvailableValue)
  }, 300)

  const handleDrag = debounce(
    () => {
      if (!isDragging) {
        setIsDragging(true)
      }
      isSearchThisAreaEnabled && setIsSearchThisAreaAvailable(true)
    },
    300,
    { leading: true, trailing: false },
  )

  const handleClick = () => {
    if (selectedMarker && onClearMarker) {
      onClearMarker()
    }
  }

  const handleSearchThisArea = () => {
    if (typeof onSearchThisArea === 'function' && radius !== 0) {
      onSearchThisArea({ center, zoom, radius })
    }
  }

  return (
    <div
      style={{ position: 'relative', paddingBottom: '75%' }}
      className="fr-ec-mb-spacing-05"
    >
      <div
        className={className}
        style={{
          position: 'absolute',
          ...style,
          ...dimensions,
          height: '100%',
        }}
      >
        {isSearchThisAreaEnabled && isSearchThisAreaAvailable && (
          <Button
            element="button"
            variant="secondary"
            icon="search"
            style={{
              position: 'absolute',
              zIndex: 1,
              width: 'auto',
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
            isSmall={true}
            onClick={handleSearchThisArea}
          >
            {searchThisArea}
          </Button>
        )}
        <ErrorBoundary>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              zoom={zoom}
              center={{
                lat: center.latitude,
                lng: center.longitude,
              }}
              options={{
                gestureHandling: 'greedy',
                streetViewControl: false,
                mapTypeControl: false,
              }}
              onClick={handleClick}
              onZoomChanged={handleZoomChange}
              onCenterChanged={handleCenterChanged}
              onBoundsChanged={handleBoundsChanged}
              onDrag={handleDrag}
              onLoad={(map: google.maps.Map) => {
                setGoogleMap(map)
              }}
            >
              {storeList.map(store => (
                <MapMarker
                  key={store.id}
                  location={{
                    lat: Number(store.latitude),
                    lng: Number(store.longitude),
                  }}
                  store={store}
                  getDirectionsLabel={getDirections}
                  kmAwayLabel={distanceAway}
                  handleStoreSelect={onStoreSelect ? onStoreSelect : () => {}}
                  handleMarkerClick={onMarkerSelect ? onMarkerSelect : () => {}}
                  selectedMarker={selectedMarker}
                />
              ))}
            </GoogleMap>
          ) : (
            <></>
          )}
        </ErrorBoundary>
      </div>
    </div>
  )
}

export default StoresMap
